import EditIcon from "@mui/icons-material/Edit"
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { useFormik } from "formik"
import { useAppDispatch } from "hooks"
import { DropzoneArea } from "mui-file-dropzone"
import { useEffect, useState } from "react"
import chapterService from "services/chapterService"
import lessonService from "services/lessonService"
import { setLessons, updateLesson } from "slices/lessonSlice"
import type { IChapter } from "types/chapterTypes"
import { type ILesson, type IUpdateLesson, LessonType } from "types/lessonTypes"
import * as Yup from "yup"

type Props = {
  lesson: ILesson
}

export function UpdateLessonDialog({ lesson }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)
  const [chapters, setChapters] = useState<Array<IChapter>>([])

  const dispatch = useAppDispatch()

  useEffect(() => {
    chapterService
      .getChapters()
      .then((resp) => {
        setChapters(resp)
      })
      .catch((err) => {
        console.log(err)
      })
  }, [])

  const initialValues: IUpdateLesson = {
    type: lesson.type,
    name: lesson.name,
    description: lesson.description,
    imageFile: undefined,
    chapterId: lesson.chapterId,
  }

  const updateLessonValidationScheme = Yup.object({
    type: Yup.string().required("Please enter lesson type"),
    name: Yup.string()
      .required("Please enter lesson name")
      .max(64, "Maximum name length is 64 characters"),
    description: Yup.string()
      .required("Please enter lesson description")
      .max(256, "Maximum description length is 256 characters"),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: updateLessonValidationScheme,
    onSubmit: (values) => {
      setIsLoading(true)

      const lessonUpdate: IUpdateLesson = {
        type: values.type,
        name: values.name,
        description: values.description,
        imageFile: values.imageFile,
        chapterId: values.chapterId,
      }

      lessonService
        .updateLesson(lesson.id, lessonUpdate)
        .then((resp) => {
          dispatch(updateLesson(resp))
        })
        .then(() => lessonService.getChapterLessons(initialValues.chapterId)) //need to refresh lessons here in case one was moved to another chapter
        .then((resp2) => {
          dispatch(setLessons(resp2))
          setIsLoading(false)
          handleClose()
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div>
      <MenuItem onClick={handleClickOpen}>
        <EditIcon sx={{ marginRight: 1 }} />
        Update
      </MenuItem>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>Update Lesson</DialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  margin="dense"
                  id="type"
                  label="Type"
                  type="type"
                  name="type"
                  select
                  variant="outlined"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  error={formik.touched.type && Boolean(formik.errors.type)}
                  helperText={formik.touched.type && formik.errors.type}
                >
                  <MenuItem key={1} value={LessonType.Lesson.toString()}>
                    Lesson
                  </MenuItem>
                  <MenuItem key={2} value={LessonType.Exam.toString()}>
                    Exam
                  </MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  autoFocus
                  margin="dense"
                  id="name"
                  label="Name"
                  type="name"
                  fullWidth
                  variant="outlined"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="chapter-id">Chapter *</InputLabel>
                  <Select
                    id="chapterId"
                    labelId="chapter-id"
                    label="Chapter *"
                    name="chapterId"
                    value={formik.values.chapterId}
                    onChange={formik.handleChange}
                    error={formik.touched.chapterId && Boolean(formik.errors.chapterId)}
                  >
                    {chapters.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  margin="dense"
                  id="description"
                  label="Description"
                  type="description"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={4}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                />
              </Grid>
              <Grid item xs={12}>
                <DropzoneArea
                  onChange={(files) => {
                    formik.setFieldValue("imageFile", files[0])
                  }}
                  filesLimit={1}
                  fileObjects={formik.values.imageFile}
                  acceptedFiles={["image/jpeg", "image/png", "image/bmp"]}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading}>
                Update
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
